// CreateArea.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createArea,
  getAllAreas,
  getAreaByID,
  areaImageUpload,
  updateArea,
} from "./services/area.service";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getFile } from "../../_helper/helper";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCities } from "../city/services/city.services";
import { clearAreaDetails } from "./areaSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { findIndex } from "lodash";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");
  const cityList = useAppSelector((state) => state.city.CityList);

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const stateIndex = findIndex(cityList, { ID: CityID });
    if (stateIndex > -1) {
      const state = cityList[stateIndex];
      formRenderProps.onChange("StateID", { value: state.StateID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateArea: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const AreaID = useAppSelector((state) => state.drawer.data);
  const AreaDetail = useAppSelector((state) => state.area.AreaDetail);
  const loading = useAppSelector((state) => state.area.formLoading);
  const [formKey, setFormKey] = useState(1);
  const stateList = useAppSelector((state) => state.state.StateList);
  const cityList = useAppSelector((state) => state.city.CityList);

  useEffect(() => {
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCities());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AreaDetail]);

  useEffect(() => {
    if (AreaID) {
      dispatch(getAreaByID(AreaID));
    }
    return () => {
      dispatch(clearAreaDetails());
    };
  }, [AreaID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      ShortDescription: values.ShortDescription ?? "",
      IsDisplayInHome: values.IsDisplayInHome ?? false,
      SequenceNo: values.SequenceNo ?? 0,
      CityID: values.CityID ?? 0,
      StateID: values.StateID ?? 0,
      IsActive: values.IsActive === 1,
    };

    const ImageData = new FormData();
    const ImageUpload =
      values?.ImageUpload && typeof values?.ImageUpload !== "string"
        ? getFile(values?.ImageUpload[0])
        : "";

    try {
      if (AreaID) {
        const editPayload = {
          ...formData,
          ID: AreaID,
        };

        const response = await dispatch(updateArea(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (values?.ImageUpload && typeof values?.ImageUpload !== "string") {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", AreaID);
            await dispatch(areaImageUpload(ImageData));
          }
          dispatch(getAllAreas());
          dispatch(clearAreaDetails());
        }
      } else {
        const response = await dispatch(createArea(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.ImageUpload &&
            typeof values?.ImageUpload !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(areaImageUpload(ImageData));
          }
          dispatch(getAllAreas());
          dispatch(clearAreaDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={AreaDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <CityChangeWatcher formRenderProps={formRenderProps} />
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {AreaID ? "Update Area" : "Add Area"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Area Title"
                    maxLength="200"
                    placeholder="Enter Area title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    maxLength="500"
                    placeholder="Enter short description"
                    component={FormTextArea}
                    style={{ height: 100 }}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CityID"
                    label="City"
                    placeholder="Select City"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={cityList?.map((city: any) => ({
                      value: city.ID,
                      label: city.CityName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="StateID"
                    label="State"
                    placeholder="Select State"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    disabled={true}
                    options={stateList?.map((state: any) => ({
                      value: state.ID,
                      label: state.StateName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="SequenceNo"
                    label="Sequence Number"
                    placeholder="Enter sequence number"
                    component={FormNumericTextField}
                    min={0}
                    max={999}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={1}
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsDisplayInHome"
                    label="Is Displayed in Home"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="ImageUpload"
                    label="Area Image"
                    type="file"
                    alternateName="ImageUpload"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status?.value,
                      label: status?.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={AreaID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateArea;
